import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { average, groupCountBy, groupSumBy, groupSumByMonth } from '@/lib/dashboardAggregations';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { GrnRegisterRow, PutawayTatRow, QcAnalysisRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#4edea3', '#ba1a1a', '#ffb951', '#0566d9', '#6b6f76'];

export default function InboundQualitySlide() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();
  const { data: processConfiguration } = useProcessConfiguration();
  // Mirrors InboundQualityPanel.tsx's sub-flag gating — QC/Putaway are individually toggleable.
  const qcEnabled = processConfiguration ? processConfiguration.qcEnabled : true;
  const putawayEnabled = processConfiguration ? processConfiguration.putawayEnabled : true;

  const { data: grns = [] } = useQuery({
    queryKey: ['reports', 'grn-register'],
    queryFn: async () => (await apiClient.get<GrnRegisterRow[]>('/reports/grn-register')).data,
    refetchInterval: 60_000,
  });
  const { data: qcAnalysis = [] } = useQuery({
    queryKey: ['reports', 'qc-analysis'],
    queryFn: async () => (await apiClient.get<QcAnalysisRow[]>('/reports/qc-analysis')).data,
    refetchInterval: 60_000,
  });
  const { data: putawayTat = [] } = useQuery({
    queryKey: ['reports', 'putaway-tat'],
    queryFn: async () => (await apiClient.get<PutawayTatRow[]>('/reports/putaway-tat')).data,
    refetchInterval: 60_000,
  });

  const totalGrns = useMemo(() => new Set(grns.map((r) => r.grnId)).size, [grns]);
  const acceptedQty = useMemo(() => grns.reduce((sum, r) => sum + r.acceptedQty, 0), [grns]);
  const rejectedQty = useMemo(() => grns.reduce((sum, r) => sum + r.rejectedQty, 0), [grns]);
  const avgTat = useMemo(() => average(putawayTat.map((r) => r.tatHours).filter((v): v is number => v != null)), [putawayTat]);

  const monthlyAcceptReject = useMemo(
    () => groupSumByMonth(grns, (r) => r.grnDate, [
      { key: 'accepted', value: (r) => r.acceptedQty },
      { key: 'rejected', value: (r) => r.rejectedQty },
    ]),
    [grns],
  );
  const qcStatusDistribution = useMemo(() => groupCountBy(qcAnalysis, (r) => r.status), [qcAnalysis]);
  const topRejected = useMemo(
    () => groupSumBy(grns.filter((r) => r.rejectedQty > 0), (r) => `${r.materialCode} — ${r.materialName}`, (r) => r.rejectedQty).slice(0, 5),
    [grns],
  );

  const kpiCount = 3 + (putawayEnabled ? 1 : 0);
  const chartColumns = qcEnabled ? '1.1fr 0.8fr 0.9fr' : '1.4fr 0.9fr';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${kpiCount}, 1fr)`, gap: 2 }}>
        <TvKpiTile label={t('inboundQuality.totalGrns')} value={totalGrns} icon={<MoveToInboxIcon />} accentColor={colors.inboundQuality} />
        <TvKpiTile label={t('inboundQuality.acceptedQty')} value={acceptedQty.toLocaleString()} icon={<CheckCircleOutlineIcon />} accentColor={colors.outbound} />
        <TvKpiTile label={t('inboundQuality.rejectedQty')} value={rejectedQty.toLocaleString()} icon={<CancelOutlinedIcon />} accentColor={colors.adjustments} />
        {putawayEnabled && (
          <TvKpiTile label={t('inboundQuality.avgPutawayTat')} value={avgTat > 0 ? `${avgTat.toFixed(1)}h` : '—'} icon={<TimerOutlinedIcon />} accentColor={colors.inboundQuality} />
        )}
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: chartColumns, gap: 2, minHeight: 0 }}>
        <ChartCard title={t('inboundQuality.acceptRejectTitle')} subtitle={t('inboundQuality.acceptRejectSubtitle')} height={420}>
          <DashboardBarChart
            categories={monthlyAcceptReject.map((m) => m.month)}
            series={[
              { label: tr('columns.acceptedQty'), data: monthlyAcceptReject.map((m) => m.values.accepted), color: colors.outbound },
              { label: tr('columns.rejectedQty'), data: monthlyAcceptReject.map((m) => m.values.rejected), color: colors.adjustments },
            ]}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        {qcEnabled && (
          <ChartCard title={t('inboundQuality.qcStatusTitle')} height={420}>
            <DashboardPieChart
              data={qcStatusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))}
              height={420}
              legendPosition="bottom"
            />
          </ChartCard>
        )}
        <TvTopList title={t('inboundQuality.topRejectedTitle')} accentColor={colors.adjustments} items={topRejected.map((r) => ({ label: r.key, value: r.total }))} />
      </Box>
    </Box>
  );
}
